import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { CircularProgress, TextField } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { debounce } from 'lodash';
import { Autocomplete } from '@material-ui/lab';

import { actions as mapActions } from '../../../../store/ducks/yaMap.duck';
import { IAppState } from '../../../../store/rootDuck';
import { IStore } from '../../../../interfaces/store';
import { ILocation } from '../../../../interfaces/locations';
import PickupLocationSelect from './PickupLocationSelect';
import { IOrder } from '../../../../interfaces/order';

const LIMIT_INTERVAL = 2000;

export const AutocompleteSelf = ({
  formik,
  setPickupLocation,
  shippingLocation,
  chosenCdekCity,
  setChosenCdekCity,
  readOnly,
  order
}: {
  formik: any;
  setPickupLocation: (pickupLocation: IStore | null) => void;
  shippingLocation: ILocation | null;
  chosenCdekCity: any;
  setChosenCdekCity: any;
  readOnly?: boolean;
  order?: IOrder;
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { cart } = useSelector(
    ({ cart }: IAppState) => ({
      cart: cart.cart,
    }),
    shallowEqual
  );
  const { pointsCdek, loadingCdek, cdekCities, loadingCdekCities } = useSelector(
    ({ yaMap }: IAppState) => ({
      pointsCdek: yaMap.pickupPointsCdek,
      loadingCdek: yaMap.loadingCdek,
      cdekCities: yaMap.cdekCities,
      loadingCdekCities: yaMap.loadingCdekCities,
    })
  );

  const [location, setLocation] = useState<string>('');
  const [fetchedLocation, setFetchedLocation] = useState('');
  const [selectedPoint, setSelectedPoint] = useState<any>({
    latitude: shippingLocation?.lat ?? 55.751244,
    longitude: shippingLocation?.lng ?? 37.618423,
  });

  const [cdekCitiesToShow, setCdekCitiesToShow] = useState<any>([]);

  useEffect(() => {
    if (cart?.point) {
      setSelectedPoint({
        latitude: cart?.point.lat,
        longitude: cart?.point.lng,
        city: cart?.point.city_name,
        address: cart?.point.address,
        address_full: cart?.point.address,
        code: cart?.point.cdek_code,
      });
    }
  }, [cart]);

  useEffect(() => {
    if (order?.point) {
      setSelectedPoint({
        latitude: order?.point.lat,
        longitude: order?.point.lng,
        city: order?.point.city_name,
        address: order?.point.address,
        address_full: order?.point.address,
        code: order?.point.cdek_code,
      });
    }
  }, [order]);

  useEffect(() => {
    setCdekCitiesToShow(
      cdekCities
        ?.filter((i: any) => i.country_code === 'RU')
        .map((x: any) => ({ ...x, label: x.city, key: x.uuid })) || []
    );
  }, [cdekCities]);

  const firstTenItems = pointsCdek?.data?.slice(0, 200);

  const { values, errors, setFieldValue } = formik;

  const handleInputChange = (_e: any, _val: string, reason: any) => {
    if (reason === 'clear') {
      setChosenCdekCity(null);
    } else if (reason === 'input') {
      setLocation(_val);
    }
  };

  const fetchDebouncedLocations = debounce(() => {
    if (location.length >= 3 && location !== fetchedLocation) {
      dispatch(mapActions.fetchRequestCdekCities(location));
      setFetchedLocation(location);
    }
  }, LIMIT_INTERVAL);

  useEffect(() => {
    fetchDebouncedLocations();
    return () => fetchDebouncedLocations.cancel();
  }, [location]);

  return (
    <>
      <Autocomplete
        options={cdekCitiesToShow}
        autoHighlight
        value={chosenCdekCity?.label ?? ''}
        onChange={(_, value) => {
          setChosenCdekCity(value);
          setSelectedPoint({
            latitude: value?.latitude,
            longitude: value?.longitude,
          });
        }}
        onInputChange={handleInputChange}
        noOptionsText='Пусто'
        disabled={readOnly}
        filterOptions={o => o}
        renderOption={(props, option: any) => {
          return (
            <li {...option} key={props.code}>
              {props.full_name || props.label}
            </li>
          );
        }}
        renderInput={params => {
          return (
            <TextField
              {...params}
              type='text'
              margin='normal'
              name='location'
              variant='outlined'
              disabled={readOnly}
              error={!readOnly && !chosenCdekCity}
              helperText={!readOnly && !chosenCdekCity && 'Выберите населенный пункт'}
              label={intl.formatMessage({
                id: 'STORE.INPUT.CDEKLOCATION',
              })}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {(loadingCdekCities ||
                      (location && location.length >= 3 && location !== fetchedLocation)) && (
                      <CircularProgress color='inherit' size={20} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          );
        }}
      />
      {chosenCdekCity ? (
        <>
          {!firstTenItems || loadingCdek || loadingCdekCities ? (
            <CircularProgress size={30} />
          ) : (
            <PickupLocationSelect
              value={selectedPoint}
              isLoading={loadingCdek}
              onChange={(store: any) => {
                setPickupLocation(store);
                setSelectedPoint(store.location);
                setFieldValue('location', store.location);
              }}
              stores={firstTenItems}
              error={!readOnly && !values.location?.code}
              helperText={(!readOnly && !values.location?.code && errors.location?.code) || ''}
              intl={intl}
              disabled={readOnly}
            />
          )}
        </>
      ) : null}
    </>
  );
};
